import { Injectable } from '@angular/core';
import { BaseProvider } from './base';
import { JobNotification, MULTI_FILE_NOTIFICATION_NAME } from './job';

export interface UploadNotification {
    filename: string;
    message: string;
    success: boolean;

    /**
     * True if the message contains warnings to be displayed to the user
     */
    hasWarnings?: boolean;
    errorCode?: string;
}

export enum FileNotificationMessageType {
    ERROR = 'ERROR',
    WARNING = 'WARNING',
    SUCCESS = 'SUCCESS'
}

export interface FileNotificationMessage {
    message: string;
    messageType: FileNotificationMessageType;
    errorCode?: string;
}

export interface FileNotification {
    file: string;
    messages: FileNotificationMessage[];
    hasMoreMessages?: boolean;
    excludedFromImport?: boolean;
}

interface UploadNotificationMap {
    [key: string]: UploadNotification;
}

export interface UploadNotificationStats {
    /**
     * Number of files successfully uploaded, including files that were
     * uploaded with warnings
     */
    numberOfFilesUploaded: number;

    /**
     * Number of files that did not get uploaded
     */
    numberOfFilesFailedToUpload: number;

    /**
     * True if there are warning messages for some or all files
     */
    areWarningsPresent: boolean;
}

@Injectable()
export class UploadNotificationProvider extends BaseProvider {
    /**
     * Return an object containing a summary for the specified jobNotifications
     */
    public getUploadNotificationStats(jobNotifications: JobNotification[]): UploadNotificationStats {
        const stats: UploadNotificationStats = {
            numberOfFilesUploaded: 0,
            numberOfFilesFailedToUpload: 0,
            areWarningsPresent: false
        };

        const mergedUploadNotifications = this.getMergedUploadNotifications(jobNotifications);

        for (const notification of mergedUploadNotifications) {
            if (notification.filename == MULTI_FILE_NOTIFICATION_NAME) {
                if (!notification.success) {
                    stats.areWarningsPresent = true;
                }
            } else {
                if (notification.success) {
                    stats.numberOfFilesUploaded++;
                } else {
                    stats.numberOfFilesFailedToUpload++;
                }

                if (notification.hasWarnings) {
                    stats.areWarningsPresent = true;
                }
            }
        }

        return stats;
    }

    /**
     * Accepts an array of JobNotifications, each containing a message for a specific upload filename.
     * Convert each JobNotification to an UploadNotification. For each unique filename, merges later
     * notifications with earlier notifications. Return an array of unique filenames and the combined
     * upload messages for each
     */
    public getMergedUploadNotifications(jobNotifications: JobNotification[]): UploadNotification[] {
        const uploadNotifications = this.parseUploadNotifications(jobNotifications);

        const notificationMap: UploadNotificationMap = {};

        for (const uploadNotification of uploadNotifications) {
            const fileKey = uploadNotification.filename;

            if (notificationMap[fileKey]) {
                notificationMap[fileKey] = this.mergeUploadNotifications(notificationMap[fileKey], uploadNotification);
            } else {
                notificationMap[fileKey] = uploadNotification;
            }
        }

        return Object.getOwnPropertyNames(notificationMap).map(fileKey => notificationMap[fileKey]);
    }

    /**
     * Combine property values from the specified laterNotification with properties of the specified
     * formerNotification and return the resulting merged UploadNotification.
     */
    private mergeUploadNotifications(
        formerNotification: UploadNotification,
        laterNotification: UploadNotification
    ): UploadNotification {
        if (laterNotification.filename != formerNotification.filename) {
            throw new Error(
                `Cannot merge upload notification with filename = ${laterNotification.filename} with notification with filename = ${formerNotification.filename}. filename property must be the same for both notifications being merged`
            );
        }

        return {
            filename: laterNotification.filename,
            message: this.mergeUploadNotificationMessages(formerNotification.message, laterNotification.message),

            // Latest success status always overrides earlier statuses
            success: laterNotification.success,

            // Set warning flag to indicate warning messages that need to be displayed
            // to the user
            hasWarnings: !formerNotification.success
        };
    }

    /**
     * Append the specified upload laterMessage string to the specified formerMessage
     */
    private mergeUploadNotificationMessages(formerMessage: string, laterMessage: string): string {
        return `${formerMessage}${laterMessage} <br />`;
    }

    /**
     * Return an array of upload specific job notifications parsed
     * from the specified job notifications
     */
    private parseUploadNotifications(jobNotifications: JobNotification[]): UploadNotification[] {
        const uploadNotifications: UploadNotification[] = [];

        for (const notification of jobNotifications) {
            const uploadNotification = this.convertToUploadNotification(notification);

            if (uploadNotification) {
                uploadNotifications.push(uploadNotification);
            }
        }

        return uploadNotifications;
    }

    /**
     * Get an upload specific job notification, including details
     * about the success of the uploaded file and reason for any
     * individual file failure. Return null if an unexpected message
     * format is encountered
     */
    private convertToUploadNotification(jobNotification: JobNotification): UploadNotification | null {
        // The notification.message string is formatted as follows:
        // [non-json file name] {Back end file notification (json)}
        const filePartRegex = /^[^\{\}]+(?=\s\{)/g;
        const match = jobNotification.message.match(filePartRegex);

        if (!match || !match.length) {
            return null;
        }

        const filePart = match[0];

        const fileNotificationJsonPart = jobNotification.message.replace(filePartRegex, '');

        const fileNotification = this.parseFileNotification(fileNotificationJsonPart);

        if (fileNotification && fileNotification.messages) {
            let message = '';

            for (const subMessage of fileNotification.messages) {
                message = this.mergeUploadNotificationMessages(message, subMessage.message);
            }
            if (fileNotification.hasMoreMessages) {
                message += '...\n';
            }

            const uploadNotification: UploadNotification = {
                filename: filePart,
                message,
                success: jobNotification.success
            };

            if (fileNotification.messages[0].errorCode) {
                uploadNotification.errorCode = fileNotification.messages[0].errorCode;
            }

            return uploadNotification;
        } else {
            return {
                filename: filePart,
                message: jobNotification.message,
                success: jobNotification.success
            };
        }
    }

    /**
     * Parse a FileNotification from a json string
     */
    private parseFileNotification(fileNotificationJson: string): FileNotification | null {
        try {
            return JSON.parse(fileNotificationJson);
        } catch (err) {
            console.log('Failed to parse file detail as json. Treating as a string message instead');
            return null;
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/upload-notification.ts